import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Pane, Popup, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
    AlertTriangle, BoxSelect, CalendarClock, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, Download, FileSpreadsheet, Filter, Loader2,
    MapPin, Maximize2, Minimize2, Plus, Search, ShieldAlert, Sparkles, Upload, UserCheck, Users, X,
} from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { supabase } from '../../../src/lib/supabase';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import SearchableSelect from '../../../src/components/SearchableSelect';

/** Chave de cidade sem acento — "Goiania" e "Goiânia" são a mesma cidade. */
const chaveCidade = (c?: string | null) =>
    (c || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();
import RotaConfigModal, { RotaConfig } from './RotaConfigModal';
import ProspeccaoRowModal from './ProspeccaoRowModal';
import MapAutoResize from './MapAutoResize';
import { exportarParaCnpjBiz } from '../../../src/services/exportarCnpjBiz';
import { CUSTO_GEOCODE_USD } from '../../../src/services/geocodeLog';
import ExportCnpjBizModal from './ExportCnpjBizModal';
import AgendarLoteModal, { AgendamentoConfig } from './AgendarLoteModal';
import {
    StagedRow, DonoInfo, FirstStage, onlyDigits, maskDoc, docTipoFromDigits,
    parsePlanilha, baixarPlanilhaModelo, checarDuplicidade, gravarStaging, carregarStaging,
    enriquecerRow, marcarErroImport, proximoPassoEnriquecimento, temEnderecoParaGeocode,
    importarStagedLead, agendarVisitasComLeads, LinhaImport, linhaParcial, garantirCompleta, hidratarLinhas,
} from '../../../src/services/prospeccaoLista';
import { criarJob, getCotaLemit, getJanelaNoturna, noitesEstimadas } from '../../../src/services/prospeccaoFila';
import { funilDeVendas } from '../../../src/lib/pipelines';

// `criadoDia`: dia local (AAAA-MM-DD) em que o lead entrou na lista — calculado
// uma vez na carga para o filtro de data não refazer 11 mil `new Date()` por tecla.
type ViewRow = StagedRow & { dono?: DonoInfo | null; importadoPara?: { id: string; nome: string } | null; criadoDia?: string };
type FiltroStatus = 'todos' | 'disponivel' | 'importado' | 'erro' | 'duplicado' | 'sem_geo' | 'com_geo';
type FiltroEnriq = 'todos' | 'nao' | 'empresa' | 'desempate' | 'completo' | 'falhou';

/**
 * O dono foi decidido no cara ou coroa: a Lemit não informou o rateio (devolve
 * `participacao: 0` em todos) e ficou o primeiro da lista. É o único caso em que
 * vale um humano conferir — daí o chip próprio.
 */
const ehDesempate = (r: StagedRow): boolean =>
    r.enriquecimento_data?.representante_criterio === 'ordem_lemit';

const PAGE_SIZES = [25, 50, 100, 200, 500];
const fmtBRL = (v?: number | null) => (v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—');
const fmtFone = (d?: string | null) => {
    const n = onlyDigits(d);
    if (n.length < 10) return d || '—';
    const ddd = n.slice(0, 2), resto = n.slice(2);
    return `(${ddd}) ${resto.slice(0, resto.length - 4)}-${resto.slice(-4)}`;
};
// Segmentos CONDENSADOS (buckets amigáveis) mapeados a partir do prefixo do CNAE
// (4 primeiros dígitos = classe). Mesmo espírito das categorias da Busca Geográfica:
// poucas opções, multiseleção, agrupadas por setor. Um CNAE que não cai em bucket
// nenhum vira "Outros"; sem CNAE a linha fica de fora do filtro.
type SegBucket = { key: string; label: string; emoji: string; cnae4: string[] };
const GRUPOS_SEGMENTO: Array<{ grupo: string; itens: SegBucket[] }> = [
    { grupo: 'Alimentação & Bebidas', itens: [
        { key: 'restaurantes', label: 'Restaurantes & Lanchonetes', emoji: '🍽️', cnae4: ['5611', '5612', '5620'] },
        { key: 'mercados', label: 'Mercados & Supermercados', emoji: '🛒', cnae4: ['4711', '4712', '4713'] },
        { key: 'bebidas', label: 'Bebidas', emoji: '🍺', cnae4: ['4723', '4635'] },
        { key: 'padarias', label: 'Padarias', emoji: '🥖', cnae4: ['4721', '1091', '1092'] },
        { key: 'sorveterias', label: 'Sorveterias', emoji: '🍦', cnae4: ['1053', '4637'] },
        { key: 'acougues', label: 'Açougues & Peixarias', emoji: '🥩', cnae4: ['4722'] },
        { key: 'hortifruti', label: 'Hortifruti', emoji: '🥬', cnae4: ['4724'] },
        { key: 'atacado_alim', label: 'Atacado de Alimentos', emoji: '📦', cnae4: ['4639', '4631', '4632', '4633'] },
        { key: 'conveniencia', label: 'Tabacaria & Conveniência', emoji: '🚬', cnae4: ['4729'] },
    ] },
    { grupo: 'Saúde & Bem-estar', itens: [
        { key: 'clinicas', label: 'Clínicas & Consultórios', emoji: '🩺', cnae4: ['8630', '8650', '8690', '8711', '8712'] },
        { key: 'labs', label: 'Laboratórios & Imagem', emoji: '🔬', cnae4: ['8640'] },
        { key: 'hospitais', label: 'Hospitais', emoji: '🏥', cnae4: ['8610', '8621', '8622'] },
        { key: 'farmacias', label: 'Farmácias', emoji: '💊', cnae4: ['4771'] },
        { key: 'cosmeticos', label: 'Cosméticos & Perfumaria', emoji: '💄', cnae4: ['4772'] },
        { key: 'veterinarias', label: 'Veterinárias', emoji: '🐾', cnae4: ['7500'] },
        { key: 'academias', label: 'Academias', emoji: '🏋️', cnae4: ['9313'] },
        { key: 'beleza', label: 'Salões & Beleza', emoji: '💇', cnae4: ['9602'] },
    ] },
    { grupo: 'Hospedagem', itens: [
        { key: 'hoteis', label: 'Hotéis & Motéis', emoji: '🏨', cnae4: ['5510', '5590'] },
    ] },
    { grupo: 'Automotivo & Combustível', itens: [
        { key: 'postos', label: 'Postos & Gás', emoji: '⛽', cnae4: ['4731', '4732', '4784'] },
        { key: 'oficinas', label: 'Oficinas & Autopeças', emoji: '🔧', cnae4: ['4520', '4530', '4541'] },
    ] },
    { grupo: 'Imobiliário & Construção', itens: [
        { key: 'imobiliario', label: 'Imobiliárias', emoji: '🏢', cnae4: ['4110', '6810', '6821', '6822'] },
        { key: 'construcao', label: 'Construção & Materiais', emoji: '🧱', cnae4: ['4744', '4399', '4120', '4291', '4321'] },
    ] },
    { grupo: 'Serviços & Varejo', itens: [
        { key: 'contabilidade', label: 'Contabilidade', emoji: '📊', cnae4: ['6920'] },
        { key: 'escritorios', label: 'Escritórios & Adm.', emoji: '🗂️', cnae4: ['8211', '8219', '8230', '8299'] },
        { key: 'educacao', label: 'Educação', emoji: '🏫', cnae4: ['8513', '8520', '8531', '8550', '8599'] },
        { key: 'vestuario', label: 'Vestuário & Acessórios', emoji: '👕', cnae4: ['4781', '4782', '4783', '4789'] },
    ] },
];
const CNAE4_TO_BUCKET: Record<string, string> = {};
const BUCKET_META: Record<string, { label: string; emoji: string }> = { outros: { label: 'Outros', emoji: '🏷️' } };
GRUPOS_SEGMENTO.forEach((g) => g.itens.forEach((it) => {
    BUCKET_META[it.key] = { label: it.label, emoji: it.emoji };
    it.cnae4.forEach((c) => { CNAE4_TO_BUCKET[c] = it.key; });
}));

/** Filtro de segmento condensado e multiseleção (popover com chips por setor + contagem). */
const SegmentoFilter: React.FC<{ selected: string[]; onChange: (v: string[]) => void; counts: Record<string, number> }> = ({ selected, onChange, counts }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        if (open) document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);
    const toggle = (k: string) => onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
    const grupos = GRUPOS_SEGMENTO
        .map((g) => ({ grupo: g.grupo, itens: g.itens.filter((it) => (counts[it.key] || 0) > 0) }))
        .filter((g) => g.itens.length);
    const temOutros = (counts['outros'] || 0) > 0;
    return (
        <div ref={ref} className="relative shrink-0">
            <button onClick={() => setOpen((o) => !o)}
                className={`flex items-center gap-1.5 h-[42px] px-3 rounded-xl text-sm font-semibold border transition-all ${selected.length
                    ? 'bg-primary/10 border-primary/40 text-primary'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}>
                <span className="text-xs font-bold opacity-70">Segmento</span>
                {selected.length ? <span className="px-1.5 rounded-full bg-primary text-white text-[10px] font-bold">{selected.length}</span> : <span className="text-gray-400 text-xs">Todos</span>}
                <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 left-0 w-[340px] max-h-[400px] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{selected.length ? `${selected.length} selecionado(s)` : 'Escolha um ou mais ramos'}</span>
                        {selected.length > 0 && <button onClick={() => onChange([])} className="text-[11px] font-bold text-primary hover:underline">limpar</button>}
                    </div>
                    {grupos.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">Nenhum segmento — enriqueça os leads pela Lemit para classificar por CNAE.</p>
                    ) : grupos.map((g) => (
                        <div key={g.grupo}>
                            <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{g.grupo}</p>
                            <div className="grid grid-cols-2 gap-1.5">
                                {g.itens.map((it) => {
                                    const active = selected.includes(it.key);
                                    return (
                                        <button key={it.key} onClick={() => toggle(it.key)}
                                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-all text-left ${active
                                                ? 'bg-primary/10 border-primary/40 text-primary'
                                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}>
                                            <span>{it.emoji}</span>
                                            <span className="truncate flex-1">{it.label}</span>
                                            <span className={`text-[10px] ${active ? 'text-primary/70' : 'text-gray-400'}`}>{counts[it.key]}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {temOutros && (
                        <button onClick={() => toggle('outros')}
                            className={`flex items-center gap-1.5 w-full px-2 py-1.5 rounded-lg text-xs font-semibold border transition-all text-left ${selected.includes('outros')
                                ? 'bg-primary/10 border-primary/40 text-primary'
                                : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}>
                            <span>🏷️</span><span className="flex-1">Outros ramos</span><span className="text-[10px] text-gray-400">{counts['outros']}</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Filtro por data de entrada na lista ───────────────────────────────────────
// Datas como texto AAAA-MM-DD no fuso de quem olha: comparar string já ordena,
// e "hoje" tem que ser o hoje do usuário, não o do UTC (21h em Goiânia já é amanhã).
type FiltroData = { de: string; ate: string };
const FILTRO_DATA_VAZIO: FiltroData = { de: '', ate: '' };

const isoDia = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const diaLocal = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : isoDia(d);
};
const somaDias = (dia: string, n: number) => {
    const d = new Date(`${dia}T12:00:00`);
    d.setDate(d.getDate() + n);
    return isoDia(d);
};
const fmtDia = (dia: string) => {
    if (!dia) return '—';
    const [a, m, d] = dia.split('-');
    return a === String(new Date().getFullYear()) ? `${d}/${m}` : `${d}/${m}/${a.slice(2)}`;
};
const diaSemana = (dia: string) =>
    new Date(`${dia}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');

const presetsData = () => {
    const hoje = isoDia(new Date());
    const ontem = somaDias(hoje, -1);
    return [
        { l: 'Hoje', de: hoje, ate: hoje },
        { l: 'Ontem', de: ontem, ate: ontem },
        { l: 'Últimos 7 dias', de: somaDias(hoje, -6), ate: hoje },
        { l: 'Últimos 30 dias', de: somaDias(hoje, -29), ate: hoje },
    ];
};

const rotuloData = (f: FiltroData): string => {
    const p = presetsData().find((x) => x.de === f.de && x.ate === f.ate);
    if (p) return p.l;
    if (f.de && f.ate) return f.de === f.ate ? fmtDia(f.de) : `${fmtDia(f.de)} – ${fmtDia(f.ate)}`;
    return f.de ? `desde ${fmtDia(f.de)}` : `até ${fmtDia(f.ate)}`;
};

/**
 * Filtro "Adicionado em": atalhos, os dias em que houve upload (com quantos
 * entraram em cada um) e um período livre. Os uploads chegam em lotes — achar
 * "os 50 de hoje" no meio de 11 mil era impossível sem isto.
 */
const DataFilter: React.FC<{ value: FiltroData; onChange: (v: FiltroData) => void; porDia: Record<string, number> }> = ({ value, onChange, porDia }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        if (open) document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);
    const ativo = !!(value.de || value.ate);
    const contar = (de: string, ate: string) =>
        Object.entries(porDia).reduce((s, [d, n]) => (d >= de && d <= ate ? s + n : s), 0);
    const dias = Object.entries(porDia).sort((a, b) => b[0].localeCompare(a[0]));
    const escolher = (v: FiltroData) => { onChange(v); setOpen(false); };
    const chip = (on: boolean) => `flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-all text-left ${on
        ? 'bg-primary/10 border-primary/40 text-primary'
        : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`;
    const inputData = 'flex-1 min-w-0 text-xs px-2 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none dark:[color-scheme:dark]';
    return (
        <div ref={ref} className="relative shrink-0">
            <button onClick={() => setOpen((o) => !o)}
                className={`flex items-center gap-1.5 h-[42px] px-3 rounded-xl text-sm font-semibold border transition-all ${ativo
                    ? 'bg-primary/10 border-primary/40 text-primary'
                    : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}>
                <CalendarDays size={14} className="opacity-70" />
                <span className="text-xs font-bold opacity-70">Adicionado</span>
                {ativo ? <span className="text-xs font-bold">{rotuloData(value)}</span> : <span className="text-gray-400 text-xs">Sempre</span>}
                <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 left-0 w-[320px] rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Quando entrou na lista</span>
                        {ativo && <button onClick={() => escolher(FILTRO_DATA_VAZIO)} className="text-[11px] font-bold text-primary hover:underline">limpar</button>}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        {presetsData().map((p) => (
                            <button key={p.l} onClick={() => escolher({ de: p.de, ate: p.ate })} className={chip(value.de === p.de && value.ate === p.ate)}>
                                <span className="truncate">{p.l}</span>
                                <span className="text-[10px] opacity-70">{contar(p.de, p.ate).toLocaleString('pt-BR')}</span>
                            </button>
                        ))}
                    </div>
                    {dias.length > 0 && (
                        <div>
                            <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Dias com upload</p>
                            <div className="max-h-[180px] overflow-y-auto space-y-1 pr-1">
                                {dias.map(([d, n]) => (
                                    <button key={d} onClick={() => escolher({ de: d, ate: d })} className={`w-full ${chip(value.de === d && value.ate === d)}`}>
                                        <span>{fmtDia(d)} <span className="font-normal opacity-60">{diaSemana(d)}</span></span>
                                        <span className="text-[10px] opacity-70">{n.toLocaleString('pt-BR')} lead(s)</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <p className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">Período</p>
                        <div className="flex items-center gap-1.5">
                            <input type="date" value={value.de} max={value.ate || undefined} onChange={(e) => onChange({ ...value, de: e.target.value })} className={inputData} />
                            <span className="text-xs text-gray-400">até</span>
                            <input type="date" value={value.ate} min={value.de || undefined} onChange={(e) => onChange({ ...value, ate: e.target.value })} className={inputData} />
                        </div>
                    </div>
                    {/* O upload não regrava quem já estava na lista (preserva enriquecimento
                        e import), então a data é a da primeira vez que o documento entrou. */}
                    <p className="text-[10px] text-gray-400 leading-snug">Documento que já estava na lista fica com a data da primeira vez que entrou.</p>
                </div>
            )}
        </div>
    );
};

// Pins por status (mesmo padrão da Busca Geográfica): laranja=disponível ·
// azul=selecionado · cinza=importado/duplicado.
const PIN_COLOR = { disponivel: '#f97316', selecionado: '#2563eb', importado: '#9ca3af' } as const;
type PinStatus = keyof typeof PIN_COLOR;
// Ordem de pintura: o que vem depois fica por cima quando os pins se sobrepõem.
// Importado por cima do disponível: são ~800 contra ~10 mil laranjas, e embaixo
// eles sumiam — o filtro "Todos" parecia ter menos importados que o "Importado".
const PIN_ORDEM: Record<PinStatus, number> = { disponivel: 0, importado: 1, selecionado: 2 };
type Pin = { r: StagedRow; st: PinStatus };
// Pin menor com o mapa afastado, senão a cidade vira uma mancha só.
const raioPin = (zoom: number) => (zoom >= 14 ? 9 : zoom >= 12 ? 7 : 5);

/**
 * Todos os pins num canvas só. Com um <Marker> (um <div> por ponto) o mapa não
 * aguentava as 11 mil linhas e cortava nas 1.000 primeiras da lista — no filtro
 * "Todos" quase todos os importados ficavam de fora e o mapa desmentia os filtros.
 */
const PinsCanvas: React.FC<{ pins: Pin[]; onClick: (placeId: string) => void }> = ({ pins, onClick }) => {
    const map = useMap();
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;
    // Um renderer só: cada L.canvas() novo fica pendurado no mapa como outro <canvas>.
    const renderer = useMemo(() => L.canvas({ padding: 0.5 }), []);
    useEffect(() => () => { renderer.remove(); }, [renderer]);
    useEffect(() => {
        const camada = L.layerGroup();
        const circulos: L.CircleMarker[] = [];
        const raio = raioPin(map.getZoom());
        for (const { r, st } of pins) {
            circulos.push(L.circleMarker([Number(r.latitude), Number(r.longitude)], {
                renderer, radius: raio, color: '#fff', weight: raio > 5 ? 2 : 1, fillColor: PIN_COLOR[st], fillOpacity: 1,
            }).on('click', () => onClickRef.current(r.place_id)).addTo(camada));
        }
        const ajustarRaio = () => {
            const novo = raioPin(map.getZoom());
            for (const c of circulos) c.setRadius(novo).setStyle({ weight: novo > 5 ? 2 : 1 });
        };
        camada.addTo(map);
        map.on('zoomend', ajustarRaio);
        return () => { map.off('zoomend', ajustarRaio); camada.remove(); };
    }, [map, renderer, pins]);
    return null;
};

// Observa o enquadramento do mapa (p/ "selecionar pins na área visível").
const BoundsWatcher: React.FC<{ onBounds: (b: L.LatLngBounds) => void }> = ({ onBounds }) => {
    const map = useMapEvents({ moveend: () => onBounds(map.getBounds()), zoomend: () => onBounds(map.getBounds()) });
    useEffect(() => { onBounds(map.getBounds()); }, []);
    return null;
};

const FlyTo: React.FC<{ target: [number, number] | null }> = ({ target }) => {
    const map = useMap();
    useEffect(() => { if (target) map.flyTo(target, map.getZoom() < 11 ? 11 : map.getZoom(), { duration: 0.6 }); }, [target?.[0], target?.[1]]);
    return null;
};

/** Nome/id do responsável (vendedor) das negociações importadas — p/ o filtro "importado para". */
async function carregarImportadoPara(negIds: number[]): Promise<Record<number, { id: string; nome: string }>> {
    const out: Record<number, { id: string; nome: string }> = {};
    const ids = Array.from(new Set(negIds.filter(Boolean)));
    if (ids.length === 0) return out;
    const negResp: Array<{ id: number; responsavel_id: string | null }> = [];
    for (let i = 0; i < ids.length; i += 300) {
        const { data } = await supabase.from('negociacoes').select('id, responsavel_id').in('id', ids.slice(i, i + 300));
        (data || []).forEach((n: any) => negResp.push(n));
    }
    const respIds = Array.from(new Set(negResp.map((n) => n.responsavel_id).filter(Boolean))) as string[];
    const nomes: Record<string, string> = {};
    for (let i = 0; i < respIds.length; i += 300) {
        const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', respIds.slice(i, i + 300));
        (profs || []).forEach((p: any) => { nomes[String(p.id)] = p.full_name; });
    }
    negResp.forEach((n) => { if (n.responsavel_id) out[n.id] = { id: String(n.responsavel_id), nome: nomes[String(n.responsavel_id)] || '—' }; });
    return out;
}

interface ProspeccaoUploadViewProps {
    /**
     * Muda quando um job da fila termina (o banner fica em ProspeccaoView, acima
     * das abas). Serve de gatilho para recarregar a lista com o resultado.
     */
    refreshKey?: number;
}

const ProspeccaoUploadView: React.FC<ProspeccaoUploadViewProps> = ({ refreshKey = 0 }) => {
    const { profile } = useAuth();
    const { addToast } = useToast();
    const isAllowed = profile?.role === 'adm' || profile?.role === 'gerente';

    const [rows, setRows] = useState<ViewRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [parsing, setParsing] = useState(false);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [firstStage, setFirstStage] = useState<FirstStage | null>(null);
    const [vendedoresImport, setVendedoresImport] = useState<string[]>([]);
    const [selPlaces, setSelPlaces] = useState<string[]>([]);
    const [showRotaConfig, setShowRotaConfig] = useState(false);
    const [importandoLote, setImportandoLote] = useState(false);
    const [importando, setImportando] = useState<string | null>(null);
    const [enriquecendoUm, setEnriquecendoUm] = useState<string | null>(null);
    const [custoPessoa, setCustoPessoa] = useState<number | null>(null);
    const [custoEmpresa, setCustoEmpresa] = useState<number | null>(null);
    const [manualDoc, setManualDoc] = useState('');
    const [enfileirando, setEnfileirando] = useState(false);
    // Filtros
    const [busca, setBusca] = useState('');
    const [filtroStatus, setFiltroStatus] = useState<FiltroStatus>('todos');
    const [filtroEnriq, setFiltroEnriq] = useState<FiltroEnriq>('todos');
    const [filtroResp, setFiltroResp] = useState<string[]>([]);
    const [filtroCidade, setFiltroCidade] = useState('');
    const [filtroSegmento, setFiltroSegmento] = useState<string[]>([]);
    const [filtroData, setFiltroData] = useState<FiltroData>(FILTRO_DATA_VAZIO);
    const [pagina, setPagina] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
    const [mapaExpandido, setMapaExpandido] = useState(false);
    const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
    const [destacado, setDestacado] = useState<string | null>(null);
    // Popup do pin clicado. `n` muda a cada clique para o popup reabrir mesmo no
    // pin que o usuário acabou de fechar no X.
    const [popupPin, setPopupPin] = useState<{ id: string; n: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<ViewRow | null>(null);
    const [showExport, setShowExport] = useState(false);
    const [exportandoTudo, setExportandoTudo] = useState(false);
    const [showAgendar, setShowAgendar] = useState(false);
    const fileRef = useRef<HTMLInputElement | null>(null);

    const docOf = (r: ViewRow) => onlyDigits(r.documento || r.cnpj);
    // Segmento do negócio (açougue, farmácia, padaria…) = SÓ o CNAE (ramo de atividade),
    // nunca a natureza jurídica. Fonte: CNAE do enriquecimento Lemit → CNAE da planilha
    // (hoje raro). Sem CNAE devolve '' → a linha fica FORA das opções e do filtro de segmento.
    // Segmento condensado (bucket amigável) a partir do CNAE — só quem tem CNAE entra.
    const segmentoBucketDe = (r: ViewRow): string => {
        const num = r.enriquecimento_data?.cnae?.numero;
        if (!num) return '';
        return CNAE4_TO_BUCKET[String(num).slice(0, 4)] || 'outros';
    };

    // ── carga inicial ──────────────────────────────────────────────────────────
    const recarregar = async () => {
        const staged = await carregarStaging();
        // Mapa documento → negociação que a própria linha criou, p/ não marcar como
        // "duplicado no CRM" o lead que nós mesmos importamos.
        const proprias: Record<string, number | null> = {};
        staged.forEach((r) => { proprias[onlyDigits(r.documento || r.cnpj)] = r.negociacao_id ?? null; });
        const donos = await checarDuplicidade(staged.map((r) => r.documento || r.cnpj), proprias);
        const importadoPara = await carregarImportadoPara(staged.filter((r) => r.negociacao_id).map((r) => r.negociacao_id!));
        setRows(staged.map((r) => {
            const d = onlyDigits(r.documento || r.cnpj);
            return {
                ...r, dono: donos[d] || null,
                importadoPara: r.negociacao_id ? importadoPara[r.negociacao_id] || null : null,
                criadoDia: diaLocal(r.created_at),
            };
        }));
    };

    useEffect(() => {
        if (!isAllowed) { setLoading(false); return; }
        (async () => {
            setLoading(true);
            const [{ data: pipes }, { data: profs }, { data: cfgs }] = await Promise.all([
                supabase.from('pipelines').select('id, name, pipeline_stages(name, position)').order('created_at').order('id'),
                supabase.from('profiles').select('*').neq('role', 'consumidor'),
                supabase.from('system_settings').select('key, value').in('key', ['lemit_custo_pessoa', 'lemit_custo_empresa']),
            ]);
            if (profs) setProfiles(profs);
            // funilDeVendas em vez de /venda/i: esse regex casa com "Pós-Venda" e, com os
            // dois funis empatados em created_at, o lead importado podia nascer lá.
            const vendas = funilDeVendas(pipes as any[]);
            if (vendas) {
                const stages = ((vendas as any).pipeline_stages || []).sort((a: any, b: any) => a.position - b.position);
                if (stages[0]) setFirstStage({ name: stages[0].name, pipeline_id: (vendas as any).id });
            }
            (cfgs || []).forEach((c: any) => {
                const p = parseFloat(String(c.value).replace(',', '.'));
                if (!isNaN(p)) { if (c.key === 'lemit_custo_pessoa') setCustoPessoa(p); else setCustoEmpresa(p); }
            });
            try { await recarregar(); } catch (e) { console.error(e); }
            setLoading(false);
        })();
    }, [profile?.id, isAllowed]);

    // volta p/ a 1ª página quando o filtro muda
    useEffect(() => { setPagina(0); }, [busca, filtroStatus, filtroEnriq, filtroResp, filtroCidade, filtroSegmento, filtroData]);

    // Um job da fila terminou (o banner vive acima das abas): traz o resultado.
    useEffect(() => {
        if (refreshKey === 0 || !isAllowed) return;
        recarregar().catch((e) => console.error(e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // ── upload / adicionar ──────────────────────────────────────────────────────
    const handleFile = async (file: File) => {
        setParsing(true);
        try {
            const linhas = await parsePlanilha(file);
            if (linhas.length === 0) { addToast('Nenhuma linha com CPF/CNPJ válido encontrada na planilha.', 'warning'); return; }
            const antes = new Set(rows.map((r) => docOf(r)));
            await gravarStaging(linhas);
            await recarregar();
            const novos = linhas.filter((l) => !antes.has(l.documento)).length;
            // Já abre no lote que acabou de subir; senão ele some no meio da base inteira.
            if (novos > 0) {
                const hoje = isoDia(new Date());
                setFiltroData({ de: hoje, ate: hoje });
            }
            addToast(
                `${linhas.length} linha(s) lidas · ${novos} nova(s) adicionada(s) à lista fria.`
                + (novos > 0 ? ' Mostrando as de hoje.' : ''),
                'success',
            );
        } catch (e: any) {
            console.error(e);
            addToast('Erro ao ler a planilha: ' + (e.message || 'formato inválido'), 'error');
        } finally {
            setParsing(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const adicionarManual = async () => {
        const doc = onlyDigits(manualDoc);
        if (doc.length !== 11 && doc.length !== 14) { addToast('Informe um CPF (11) ou CNPJ (14 dígitos).', 'warning'); return; }
        setParsing(true);
        try {
            const linha: LinhaImport = {
                documento: doc, docTipo: docTipoFromDigits(doc),
                razaoSocial: maskDoc(doc), nomeFantasia: '', email: '', cep: '',
                tipoLogradouro: '', logradouro: '', numero: '', complemento: '', bairro: '', estado: '',
                municipio: '', tipo: '', naturezaJuridica: 'Adicionado manualmente', telefones: [],
                linkWhatsapp: '', sociosTexto: '', enderecoCompleto: '',
            };
            await gravarStaging([linha]);
            await recarregar();
            setManualDoc('');
            addToast('Documento adicionado. Use "Enriquecer" para puxar os dados pela Lemit.', 'success');
        } catch (e: any) {
            addToast('Erro ao adicionar: ' + (e.message || ''), 'error');
        } finally {
            setParsing(false);
        }
    };

    // ── filtro/derivação ─────────────────────────────────────────────────────────
    const isImportavel = (r: ViewRow) => r.status !== 'importado' && !r.dono;

    const rowsFiltradas = useMemo(() => {
        const q = onlyDigits(busca);
        const qt = busca.trim().toLowerCase();
        return rows.filter((r) => {
            if (filtroStatus === 'disponivel' && !(r.status !== 'importado' && !r.dono && !r.import_erro)) return false;
            if (filtroStatus === 'importado' && r.status !== 'importado') return false;
            if (filtroStatus === 'erro' && !r.import_erro) return false;
            if (filtroStatus === 'duplicado' && !r.dono) return false;
            if (filtroStatus === 'sem_geo' && r.latitude != null) return false;
            if (filtroStatus === 'com_geo' && r.latitude == null) return false;
            if (filtroEnriq !== 'todos') {
                const passo = proximoPassoEnriquecimento(r);
                // 'desempate' = o dono foi escolhido no cara ou coroa (todos os sócios com a
                // mesma participação). São os únicos que valem uma conferida humana.
                if (filtroEnriq === 'desempate' && !ehDesempate(r)) return false;
                if (filtroEnriq === 'empresa' && passo !== 'buscar_dono') return false;
                if (filtroEnriq === 'nao' && passo !== 'enriquecer') return false;
                if (filtroEnriq === 'completo' && passo !== 'completo') return false;
                if (filtroEnriq === 'falhou' && passo !== 'tentar_novamente') return false;
            }
            if (filtroResp.length && !(r.importadoPara && filtroResp.includes(r.importadoPara.id))) return false;
            // Compara sem acento: lote do CNPJ BIZ vem "Goiania" e o do Google "Goiânia".
            // Comparando string exata, a base ficava partida e cidade+segmento dava zero.
            if (filtroCidade && chaveCidade(r.cidade) !== chaveCidade(filtroCidade)) return false;
            if (filtroData.de && !(r.criadoDia && r.criadoDia >= filtroData.de)) return false;
            if (filtroData.ate && !(r.criadoDia && r.criadoDia <= filtroData.ate)) return false;
            if (filtroSegmento.length) {
                const b = segmentoBucketDe(r);
                // 'sem_segmento' agrupa quem ainda não tem CNAE — antes essas linhas
                // simplesmente sumiam de qualquer filtro, sem explicação na tela.
                if (!filtroSegmento.includes(b || 'sem_segmento')) return false;
            }
            if (qt) {
                const hay = `${r.nome} ${r.razao_social || ''} ${r.cidade || ''} ${r.telefone || ''} ${docOf(r)}`.toLowerCase();
                if (!hay.includes(qt) && !(q && docOf(r).includes(q))) return false;
            }
            return true;
        });
    }, [rows, busca, filtroStatus, filtroEnriq, filtroResp, filtroCidade, filtroSegmento, filtroData]);

    // Quantos leads entraram em cada dia — alimenta os atalhos e a lista "Dias com upload".
    const contPorDia = useMemo(() => {
        const m: Record<string, number> = {};
        rows.forEach((r) => { if (r.criadoDia) m[r.criadoDia] = (m[r.criadoDia] || 0) + 1; });
        return m;
    }, [rows]);

    /** Opções de cidade/segmento com contagem (o SearchableSelect ordena alfabeticamente). */
    const contarPor = (get: (r: ViewRow) => string | null | undefined) => {
        const m = new Map<string, number>();
        rows.forEach((r) => { const c = (get(r) || '').trim(); if (c) m.set(c, (m.get(c) || 0) + 1); });
        return Array.from(m.entries()).map(([value, count]) => ({ value, label: value, count }));
    };
    // As contagens são CRUZADAS: cada filtro conta sobre o que os OUTROS filtros já
    // deixaram passar. Contando sobre a base inteira, o chip dizia "Restaurantes 334"
    // e a lista abria com 85 — os outros 249 estavam noutras cidades.
    const passaSegmento = (r: ViewRow) =>
        !filtroSegmento.length || filtroSegmento.includes(segmentoBucketDe(r) || 'sem_segmento');
    const passaCidade = (r: ViewRow) =>
        !filtroCidade || chaveCidade(r.cidade) === chaveCidade(filtroCidade);

    // Cidades agrupadas pela chave sem acento: uma "Goiânia (2.728)" em vez de duas linhas
    // quase iguais no dropdown. Vence o rótulo acentuado, que é o mais legível.
    const opcoesCidade = useMemo(() => {
        const m = new Map<string, { label: string; count: number }>();
        rows.filter(passaSegmento).forEach((r) => {
            const c = (r.cidade || '').trim();
            if (!c) return;
            const k = chaveCidade(c);
            const atual = m.get(k);
            const acentuada = /[^\x00-\x7F]/.test(c);
            m.set(k, {
                label: !atual || (acentuada && !/[^\x00-\x7F]/.test(atual.label)) ? c : atual.label,
                count: (atual?.count || 0) + 1,
            });
        });
        return Array.from(m.values()).map(({ label, count }) => ({ value: label, label, count }));
    }, [rows, filtroSegmento]);
    // Contagem por bucket de segmento (alimenta os chips do SegmentoFilter), já dentro da
    // cidade escolhida — o número do chip tem que ser o que a lista vai mostrar.
    const contBuckets = useMemo(() => {
        const m: Record<string, number> = {};
        rows.filter(passaCidade).forEach((r) => { const b = segmentoBucketDe(r) || 'sem_segmento'; m[b] = (m[b] || 0) + 1; });
        return m;
    }, [rows, filtroCidade]);

    const importaveisFiltrados = useMemo(() => rowsFiltradas.filter(isImportavel), [rowsFiltradas]);
    const totalPaginas = Math.max(1, Math.ceil(rowsFiltradas.length / pageSize));
    const paginaAtual = Math.min(pagina, totalPaginas - 1);
    const inicio = paginaAtual * pageSize;
    const pageRows = useMemo(() => rowsFiltradas.slice(inicio, inicio + pageSize), [rowsFiltradas, inicio, pageSize]);

    // ── operações em lote → fila no servidor ───────────────────────────────────
    //
    // Estes lotes rodavam num `for` aqui dentro, e trocar de tela desmontava o
    // componente e matava o processo no meio. Agora a tela só ENFILEIRA: quem
    // trabalha é a edge function `processar-fila-prospeccao`, chamada de minuto
    // em minuto pelo pg_cron. Pode fechar o navegador.
    //
    // O acompanhamento é o FilaProspeccaoBanner, montado acima das abas em
    // ProspeccaoView justamente para sobreviver à troca de aba.

    // Set, não `selPlaces.includes`: com a lista inteira selecionada (7 mil de 11 mil)
    // o `includes` dentro do filter vira 80 milhões de comparações e segura a tela.
    const alvoLote = (): ViewRow[] => {
        if (!selPlaces.length) return rowsFiltradas;
        const sel = new Set(selPlaces);
        return rowsFiltradas.filter((r) => sel.has(r.place_id));
    };

    const enfileirar = async (
        tipo: 'geocode' | 'lemit_documento' | 'lemit_dono',
        alvos: ViewRow[],
        opts: { usaCotaLemit: boolean; janelaInicio?: string | null; janelaFim?: string | null; maxPorDia?: number | null },
        mensagem: string,
    ) => {
        setEnfileirando(true);
        try {
            await criarJob({
                tipo,
                placeIds: alvos.map((r) => r.place_id),
                criadoPor: profile?.id ?? null,
                criadoPorNome: profile?.full_name ?? null,
                ...opts,
            });
            setSelPlaces([]);
            addToast(mensagem, 'success');
        } catch (e: any) {
            addToast('Erro ao enfileirar o lote: ' + (e.message || ''), 'error');
        } finally {
            setEnfileirando(false);
        }
    };

    /** Alvos de cada ação no filtro/seleção atual — alimenta o modal de agendamento. */
    const alvosPorTipo = (): Record<'geocode' | 'lemit_documento' | 'lemit_dono', ViewRow[]> => {
        const base = alvoLote();
        return {
            // Sem endereço não há o que mandar para o Google: essas linhas só têm o
            // documento e precisam passar pela Lemit antes.
            geocode: base.filter((r) => r.latitude == null && temEnderecoParaGeocode(r)),
            lemit_documento: base.filter((r) => {
                const p = proximoPassoEnriquecimento(r);
                return p === 'enriquecer' || p === 'tentar_novamente';
            }),
            lemit_dono: base.filter((r) => proximoPassoEnriquecimento(r) === 'buscar_dono'),
        };
    };

    /**
     * Geocodifica via Google — API PAGA, ~US$ 0,005 por endereço. Sem seleção isso
     * pega o filtro inteiro (10k+ linhas), então confirmamos o custo antes.
     * Roda na hora: o Google não tem teto diário, só custo.
     */
    const geocodificarLote = async () => {
        const semCoord = alvoLote().filter((r) => r.latitude == null);
        const alvos = semCoord.filter(temEnderecoParaGeocode);
        // Linha só com CPF/CNPJ não tem o que geocodificar. Elas entravam no lote e
        // eram puladas caladas — a barra chegava a 100% e o mapa não mudava nada.
        const semEndereco = semCoord.length - alvos.length;
        const avisoSemEndereco = semEndereco > 0
            ? `${semEndereco.toLocaleString('pt-BR')} linha(s) ficam de fora: só têm o CPF/CNPJ, sem endereço. `
              + `Enriqueça o documento na Lemit primeiro — o endereço vem de lá.`
            : '';
        if (alvos.length === 0) {
            addToast(avisoSemEndereco || 'Nada para geolocalizar (já têm coordenadas ou filtro vazio).', 'info');
            return;
        }
        const usd = alvos.length * CUSTO_GEOCODE_USD;
        if (!window.confirm(
            `Localizar ${alvos.length.toLocaleString('pt-BR')} endereço(s) no mapa?\n\n` +
            (avisoSemEndereco ? `${avisoSemEndereco}\n\n` : '') +
            `Isso usa a API de geocodificação do Google, que é PAGA:\n` +
            `custo estimado ~US$ ${usd.toFixed(2)} (US$ ${CUSTO_GEOCODE_USD.toFixed(3)} por endereço).\n\n` +
            `Começa agora e roda no servidor — pode trocar de tela ou fechar o navegador.\n\n` +
            `Dica: filtre ou selecione antes para geocodificar só o que vai usar.`
        )) return;
        await enfileirar('geocode', alvos, { usaCotaLemit: false },
            `${alvos.length.toLocaleString('pt-BR')} endereço(s) na fila. Rodando no servidor 📍`);
    };

    /**
     * Quanto cabe AGORA na cota da conta (500/dia menos o que o time já usou) e
     * quanto o lote custa. Um lote maior que a sobra não é recusado: ele pausa em
     * `pausado_cota` e continua amanhã sozinho.
     */
    const planoAgora = async (qtd: number, custoUnit: number | null): Promise<string> => {
        let linhaCota = '';
        try {
            const cota = await getCotaLemit();
            linhaCota = `\n\nCota da Lemit hoje: ${cota.usados}/${cota.limite} usados — cabem ${cota.restante} agora.`;
            if (qtd > cota.restante) {
                linhaCota += `\nComo são ${qtd.toLocaleString('pt-BR')}, o lote para em ${cota.restante} e continua amanhã sozinho`
                    + ` (${noitesEstimadas(qtd, cota.limite)} dia(s) no total).`;
            }
        } catch { /* sem cota, segue sem a previsão */ }
        const custo = custoUnit != null ? `\nCusto estimado: ${fmtBRL(custoUnit * qtd)} (cache de 24h não cobra).` : '';
        return `${linhaCota}${custo}\n\nRoda no servidor: pode trocar de tela ou fechar o navegador.`;
    };

    /** Etapa 1, AGORA: consulta o documento (CPF = pessoa · CNPJ = empresa). */
    const enriquecerDocumentosLote = async () => {
        const alvos = alvosPorTipo().lemit_documento;
        if (alvos.length === 0) { addToast('Nada para enriquecer neste filtro.', 'info'); return; }
        const plano = await planoAgora(alvos.length, custoEmpresa);
        if (!window.confirm(
            `Enriquecer ${alvos.length.toLocaleString('pt-BR')} documento(s) na Lemit AGORA?\n` +
            `CPF traz a pessoa · CNPJ traz a empresa e o quadro societário.${plano}`
        )) return;
        await enfileirar('lemit_documento', alvos, { usaCotaLemit: true },
            `${alvos.length.toLocaleString('pt-BR')} documento(s) na fila. Consultando agora ✨`);
    };

    /** Etapa 2, AGORA: consulta o CPF do dono (o sócio é escolhido automaticamente). */
    const enriquecerDonosLote = async () => {
        const alvos = alvosPorTipo().lemit_dono;
        if (alvos.length === 0) { addToast('Nenhuma empresa com dono pendente neste filtro.', 'info'); return; }
        const plano = await planoAgora(alvos.length, custoPessoa);
        if (!window.confirm(
            `Buscar o CPF do dono de ${alvos.length.toLocaleString('pt-BR')} empresa(s) AGORA?\n` +
            `Com mais de um sócio, fica o de maior participação.${plano}`
        )) return;
        await enfileirar('lemit_dono', alvos, { usaCotaLemit: true },
            `${alvos.length.toLocaleString('pt-BR')} empresa(s) na fila. Consultando agora 👥`);
    };

    /** Mesmo trabalho, mas no horário e no ritmo que o usuário escolher. */
    const confirmarAgendamento = async (config: AgendamentoConfig) => {
        const alvos = alvosPorTipo()[config.tipo];
        if (alvos.length === 0) return;
        await enfileirar(config.tipo, alvos, {
            usaCotaLemit: config.tipo !== 'geocode',
            janelaInicio: config.janelaInicio,
            janelaFim: config.janelaFim,
            maxPorDia: config.maxPorDia,
        }, `${alvos.length.toLocaleString('pt-BR')} item(ns) agendados para as ${config.janelaInicio} 🕘`);
        setShowAgendar(false);
    };

    /** Leads-alvo do export (seleção, senão filtro; só os com algum enriquecimento). */
    const alvosExport = useMemo(
        () => (selPlaces.length ? rowsFiltradas.filter((r) => selPlaces.includes(r.place_id)) : rowsFiltradas)
            .filter((r) => r.enriquecimento_status !== 'nao'),
        [rowsFiltradas, selPlaces],
    );
    const abrirExportCnpjBiz = () => {
        if (alvosExport.length === 0) { addToast('Nada para exportar — enriqueça os leads primeiro.', 'info'); return; }
        setShowExport(true);
    };
    const confirmarExportCnpjBiz = async ({ responsibleEmail, thermometer }: { responsibleEmail: string; thermometer: number }) => {
        try {
            // A lista está enxuta: telefones, e-mails e sócios completos vêm do banco agora
            const completos = await hidratarLinhas(alvosExport);
            const r = exportarParaCnpjBiz(completos, { responsibleEmail, tag: 'VigorHub Lista Fria', thermometer: thermometer as 1 | 2 | 3 | 4 | 5 });
            setShowExport(false);
            addToast(`${r.total} lead(s) exportados · ${r.comContatoSocio} com contato do dono → ${r.arquivo}`, 'success');
        } catch (e: any) {
            addToast('Erro ao exportar: ' + (e.message || ''), 'error');
        }
    };

    /**
     * Export CRU e completo: leva TUDO que o banco guarda dos leads do recorte
     * atual, inclusive o que hoje só existe dentro dos JSONs (quadro societário,
     * dossiê do dono, veículos, vínculos). Ao contrário do export do CNPJ Biz,
     * não filtra por enriquecimento — lead sem nada também sai, com o que tem.
     */
    const exportarTudo = async () => {
        const alvos = alvoLote();
        if (alvos.length === 0) { addToast('Nenhum lead no filtro/seleção atual.', 'info'); return; }
        // Acima de alguns milhares a geração passa de meio minuto com a aba travada.
        // Sem o aviso, parece que o botão não fez nada e o usuário clica de novo.
        if (alvos.length > 3000 && !window.confirm(
            `Exportar TUDO de ${alvos.length.toLocaleString('pt-BR')} lead(s)?\n\n`
            + 'São 10 abas (leads, sócios, telefones, e-mails, endereços, veículos, participações, '
            + 'vínculos e o JSON bruto). Nesse volume a planilha passa de 50 MB, leva cerca de um minuto '
            + 'para ficar pronta e a tela fica parada até terminar.\n\n'
            + 'Se quiser algo mais leve, filtre ou selecione antes de exportar.',
        )) return;

        setExportandoTudo(true);
        // Dá um respiro pro React pintar o "Gerando…" antes de segurar a thread.
        await new Promise((r) => setTimeout(r, 60));
        try {
            // Carrega sob demanda: o `xlsx-js-style` é ~900 KB e só serve a este botão.
            const { exportarLeadsCompleto } = await import('../../../src/services/exportarLeadsCompleto');
            const nomePorProfileId: Record<string, string> = {};
            profiles.forEach((p) => { nomePorProfileId[String(p.id)] = p.full_name || ''; });
            // "Tudo" inclui o dossiê do dono e o JSON bruto, que a lista enxuta não traz
            const completos = await hidratarLinhas(alvos);
            const res = await exportarLeadsCompleto(completos, {
                nomePorProfileId,
                contexto: selCount
                    ? `${selCount.toLocaleString('pt-BR')} lead(s) selecionados na tela`
                    : 'Filtro atual da Lista Fria',
            });
            addToast(
                `${res.leads.toLocaleString('pt-BR')} lead(s) · ${res.socios.toLocaleString('pt-BR')} sócio(s) · `
                + `${res.telefones.toLocaleString('pt-BR')} telefone(s) → ${res.arquivo}`,
                'success',
            );
            if (!res.lemitOficialOk) {
                addToast('Situação cadastral, fundação e score saíram vazios — a consulta à Lemit falhou.', 'error');
            }
        } catch (e: any) {
            addToast('Erro ao exportar: ' + (e?.message || ''), 'error');
        } finally {
            setExportandoTudo(false);
        }
    };

    const patchRow = (placeId: string, patch: Partial<ViewRow>) =>
        setRows((prev) => prev.map((r) => (r.place_id === placeId ? { ...r, ...patch } : r)));

    // Abre o detalhe e centraliza o mapa no pin (sincronização lista ↔ mapa).
    // O detalhe mostra o dossiê inteiro (telefones, sócios, dados do dono): busca a linha
    // completa e troca a enxuta por ela, na lista também.
    const openRow = (r: ViewRow) => {
        setSelectedRow(r);
        if (r.latitude != null && r.longitude != null) { setDestacado(r.place_id); setFlyTarget([Number(r.latitude), Number(r.longitude)]); }
        if (linhaParcial(r)) {
            garantirCompleta(r)
                .then((completa) => {
                    patchRow(completa.place_id, completa);
                    setSelectedRow((s) => (s && s.place_id === completa.place_id ? { ...s, ...completa } : s));
                })
                .catch((e) => addToast('Erro ao carregar o detalhe: ' + (e?.message || ''), 'error'));
        }
    };

    // Pins de TODA a lista filtrada (não só da página), na ordem de pintura.
    const pins = useMemo((): Pin[] => {
        const sel = new Set(selPlaces);
        const out: Pin[] = [];
        for (const r of rowsFiltradas) {
            if (r.latitude == null || r.longitude == null) continue;
            const st: PinStatus = (r.status === 'importado' || r.dono) ? 'importado' : sel.has(r.place_id) ? 'selecionado' : 'disponivel';
            out.push({ r, st });
        }
        return out.sort((a, b) => PIN_ORDEM[a.st] - PIN_ORDEM[b.st]);
    }, [rowsFiltradas, selPlaces]);

    const pinDestacado = useMemo(() => (destacado ? pins.find((p) => p.r.place_id === destacado) || null : null), [pins, destacado]);
    const popupRow = useMemo(() => (popupPin ? rows.find((r) => r.place_id === popupPin.id) || null : null), [rows, popupPin]);
    // Posição memorizada: o <Popup> solto reabre sempre que a referência muda.
    const popupPos = useMemo((): [number, number] | null => (
        popupRow?.latitude != null && popupRow.longitude != null ? [Number(popupRow.latitude), Number(popupRow.longitude)] : null
    ), [popupRow?.latitude, popupRow?.longitude]);

    const clicarPin = (placeId: string) => {
        setDestacado(placeId);
        setPopupPin((p) => ({ id: placeId, n: (p?.n || 0) + 1 }));
    };

    // Seleciona os importáveis dentro da área visível do mapa.
    const selecionarNaArea = () => {
        if (!mapBounds) return;
        const naArea = rowsFiltradas.filter((r) => isImportavel(r) && r.latitude != null && r.longitude != null
            && mapBounds.contains(L.latLng(Number(r.latitude), Number(r.longitude))));
        if (naArea.length === 0) { addToast('Nenhum lead importável na área visível do mapa.', 'info'); return; }
        setSelPlaces((prev) => Array.from(new Set([...prev, ...naArea.map((r) => r.place_id)])));
        addToast(`${naArea.length} lead(s) da área selecionado(s).`, 'success');
    };

    const enriquecerUm = async (row: ViewRow) => {
        setEnriquecendoUm(row.place_id);
        try {
            const upd = await enriquecerRow(row);
            patchRow(upd.place_id, upd);
            if (selectedRow?.place_id === upd.place_id) setSelectedRow((s) => (s ? { ...s, ...upd } : s));
            addToast(
                upd.enriquecimento_data?.representante_pendente
                    ? 'Empresa enriquecida. Escolha qual sócio é o representante.'
                    : 'Enriquecido pela Lemit.',
                upd.enriquecimento_data?.representante_pendente ? 'info' : 'success',
            );
        } catch (e: any) {
            addToast('Erro no enriquecimento: ' + (e.message || ''), 'error');
        } finally {
            setEnriquecendoUm(null);
        }
    };

    // ── importação p/ CRM ────────────────────────────────────────────────────────
    const destinos = (): string[] => (vendedoresImport.length ? vendedoresImport : profile ? [profile.id] : []);
    const nomeCurto = (id: string) => profiles.find((p) => String(p.id) === id)?.full_name?.split(' ')[0] || 'vendedor';
    const destNome = vendedoresImport.length === 0 ? 'você' : vendedoresImport.length === 1 ? nomeCurto(vendedoresImport[0]) : `${vendedoresImport.length} vendedores`;

    // Alvo do import: seleção manual, senão TODOS os importáveis do filtro atual (importa o máximo por padrão).
    const alvoImport = (): ViewRow[] => (selPlaces.length ? rowsFiltradas.filter((r) => selPlaces.includes(r.place_id) && isImportavel(r)) : importaveisFiltrados);

    const registrarErro = async (row: ViewRow, e: any) => {
        const msg = (e?.message || 'erro desconhecido').toString().slice(0, 300);
        patchRow(row.place_id, { import_erro: msg });
        try { await marcarErroImport(row.place_id, msg); } catch { /* best-effort */ }
    };

    const importarUm = async (row: ViewRow) => {
        if (!profile || !firstStage) return;
        setImportando(row.place_id);
        try {
            const alvo = destinos()[0];
            const negId = await importarStagedLead(row, alvo, profile.id, firstStage);
            if (negId) {
                patchRow(row.place_id, { status: 'importado', negociacao_id: negId, import_erro: null, importadoPara: { id: alvo, nome: nomeCurto(alvo) } });
                if (selectedRow?.place_id === row.place_id) setSelectedRow(null);
                addToast(`"${row.nome}" importado como lead para ${vendedoresImport.length ? nomeCurto(alvo) : 'você'}! 🎉`, 'success');
            } else addToast('Erro ao importar.', 'error');
        } catch (e: any) {
            await registrarErro(row, e);
            addToast('Erro ao importar: ' + (e.message || ''), 'error');
        } finally {
            setImportando(null);
        }
    };

    const importarLoteSimples = async () => {
        if (!profile || !firstStage) return;
        setImportandoLote(true);
        // Linha completa de uma vez (em lotes), em vez de uma consulta por lead dentro do import
        let lote: ViewRow[];
        try { lote = await hidratarLinhas(alvoImport()); }
        catch (e: any) { setImportandoLote(false); addToast('Erro ao carregar os leads: ' + (e?.message || ''), 'error'); return; }
        if (lote.length === 0) { setImportandoLote(false); return; }
        const alvos = destinos();
        let ok = 0;
        for (let i = 0; i < lote.length; i++) {
            const alvo = alvos[i % alvos.length];
            try { if (await importarStagedLead(lote[i], alvo, profile.id, firstStage)) ok++; }
            catch (e) { console.error(e); await registrarErro(lote[i], e); }
        }
        setImportandoLote(false);
        setShowRotaConfig(false);
        setSelPlaces([]);
        await recarregar();
        addToast(`${ok}/${lote.length} leads importados para ${destNome} 🎉`, ok === lote.length ? 'success' : 'error');
    };

    const distribuir = (lote: ViewRow[], alvos: string[]): Record<string, ViewRow[]> => {
        const buckets: Record<string, ViewRow[]> = {};
        alvos.forEach((a) => { buckets[a] = []; });
        lote.forEach((e, i) => buckets[alvos[i % alvos.length]].push(e));
        return buckets;
    };

    const agendarVisitas = async (config: RotaConfig) => {
        if (alvoImport().length === 0 || !profile || !firstStage) return;
        setImportandoLote(true);
        try {
            const lote = await hidratarLinhas(alvoImport());
            const alvos = destinos();
            const buckets = distribuir(lote, alvos);
            let visitas = 0;
            for (const responsavelId of alvos) {
                const grupo = buckets[responsavelId] || [];
                if (grupo.length === 0) continue;
                visitas += await agendarVisitasComLeads(grupo, responsavelId, profile.id, firstStage, config);
            }
            if (visitas === 0) throw new Error('Nenhum lead pôde ser importado.');
            setShowRotaConfig(false);
            setSelPlaces([]);
            await recarregar();
            const quando = new Date(config.data + 'T12:00:00').toLocaleDateString('pt-BR');
            addToast(`${visitas} visita(s) agendada(s) para ${destNome} em ${quando}. No dia, crie a rota em Rotas → Agenda 🗓️`, 'success');
        } catch (e: any) {
            console.error(e);
            addToast('Erro ao agendar as visitas: ' + (e.message || ''), 'error');
        } finally {
            setImportandoLote(false);
        }
    };

    const centro = useMemo((): [number, number] | null => {
        const comGeo = rowsFiltradas.find((r) => r.latitude != null && r.longitude != null);
        return comGeo ? [Number(comGeo.latitude), Number(comGeo.longitude)] : null;
    }, [rowsFiltradas]);

    // Contadores por filtro (mostrados nos chips) — calculados sobre a base completa.
    const contStatus = useMemo(() => ({
        todos: rows.length,
        disponivel: rows.filter((r) => r.status !== 'importado' && !r.dono && !r.import_erro).length,
        importado: rows.filter((r) => r.status === 'importado').length,
        erro: rows.filter((r) => !!r.import_erro).length,
        duplicado: rows.filter((r) => !!r.dono).length,
        semGeo: rows.filter((r) => r.latitude == null).length,
        comGeo: rows.filter((r) => r.latitude != null).length,
    }), [rows]);

    const contEnriq = useMemo(() => {
        const c = { todos: rows.length, nao: 0, empresa: 0, desempate: 0, completo: 0, falhou: 0 };
        rows.forEach((r) => {
            const p = proximoPassoEnriquecimento(r);
            if (p === 'enriquecer') c.nao++;
            else if (p === 'buscar_dono') c.empresa++;
            else if (p === 'completo') c.completo++;
            else if (p === 'tentar_novamente') c.falhou++;
            // Transversal: um "desempate" também é 'completo'.
            if (ehDesempate(r)) c.desempate++;
        });
        return c;
    }, [rows]);

    const stats = useMemo(() => ({
        total: rows.length,
        comGeo: rows.filter((r) => r.latitude != null).length,
        enriquecidas: rows.filter((r) => r.enriquecimento_status !== 'nao').length,
        duplicadas: rows.filter((r) => r.dono).length,
        importadas: rows.filter((r) => r.status === 'importado').length,
        erros: rows.filter((r) => r.import_erro).length,
    }), [rows]);

    if (!isAllowed) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-center px-4">
                <ShieldAlert size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
                <h2 className="text-base font-bold text-gray-700 dark:text-gray-200">Acesso restrito</h2>
                <p className="text-sm text-gray-400 mt-1 max-w-sm">A Prospecção por Lista Fria consome API paga (Lemit/Google) e está disponível apenas para administradores e gerentes comerciais.</p>
            </div>
        );
    }

    const selCount = selPlaces.length;
    const alvoImportCount = alvoImport().length;

    return (
        <div className="space-y-4 px-2 sm:px-0">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 rounded-xl"><FileSpreadsheet size={22} /></div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Lista Fria (Upload)</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Suba uma lista só com CPF/CNPJ — a Lemit completa o resto. Enriqueça, localize no mapa e gere rotas.</p>
                    </div>
                </div>
                <button onClick={baixarPlanilhaModelo} className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 transition-colors border border-emerald-200 dark:border-emerald-800">
                    <Download size={14} /> Baixar planilha modelo
                </button>
            </div>

            {/* Upload + manual */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
                    className="md:col-span-2 flex items-center justify-center gap-3 p-5 rounded-2xl border-2 border-dashed border-sky-200 dark:border-sky-800 bg-sky-50/40 dark:bg-sky-900/10"
                >
                    <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
                    <button onClick={() => fileRef.current?.click()} disabled={parsing} className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 disabled:opacity-50">
                        {parsing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Subir lista (.xlsx / .csv)
                    </button>
                    <span className="text-xs text-gray-400">só a coluna CPF/CNPJ é obrigatória · ou arraste aqui</span>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <input
                        value={manualDoc}
                        onChange={(e) => setManualDoc(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') adicionarManual(); }}
                        placeholder="Adicionar CPF ou CNPJ"
                        className="flex-1 min-w-0 text-sm px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                    />
                    <button onClick={adicionarManual} disabled={parsing} className="flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 disabled:opacity-50">
                        <Plus size={14} /> Add
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner label="Carregando lista fria..." size="sm" /></div>
            ) : rows.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                    <FileSpreadsheet size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Nenhum lead na lista fria ainda. Baixe a planilha modelo e suba sua lista.</p>
                </div>
            ) : (
                <>
                    {/* Resumo + ações em massa */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 space-y-3">
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                            {[
                                { l: 'Na lista', v: stats.total },
                                { l: 'No mapa', v: stats.comGeo },
                                { l: 'Enriquecidas', v: stats.enriquecidas },
                                { l: 'Já no CRM', v: stats.duplicadas },
                                { l: 'Importadas', v: stats.importadas },
                                { l: 'Com erro', v: stats.erros },
                            ].map((s) => (
                                <div key={s.l} className="rounded-xl bg-gray-50 dark:bg-gray-700/40 p-2">
                                    <p className="text-[10px] font-semibold text-gray-400 uppercase">{s.l}</p>
                                    <p className="text-base font-bold text-gray-900 dark:text-white">{s.v}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-gray-400">{selCount ? `${selCount} selecionado(s) · ações aplicam à seleção` : 'Sem seleção · ações aplicam ao filtro atual'}</span>
                            <div className="flex flex-wrap items-center gap-2 ml-auto">
                                <button onClick={geocodificarLote} disabled={enfileirando} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 disabled:opacity-50" title="Geocodifica pelo Google — API paga (~US$ 0,005 por endereço). Roda no servidor: pode trocar de tela.">
                                    {enfileirando ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} />} Localizar no mapa <span className="opacity-60 font-normal">($)</span>
                                </button>
                                <button onClick={enriquecerDocumentosLote} disabled={enfileirando} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 hover:bg-violet-100 disabled:opacity-50" title="Etapa 1, agora — CPF traz a pessoa · CNPJ traz a empresa e o quadro societário. Roda no servidor.">
                                    {enfileirando ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Enriquecer documento
                                </button>
                                <button onClick={enriquecerDonosLote} disabled={enfileirando} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 disabled:opacity-50" title="Etapa 2, agora — consulta o CPF do dono (com 2+ sócios fica o de maior participação). Roda no servidor.">
                                    {enfileirando ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />} Enriquecer CPF dos sócios
                                </button>
                                {/* As três ações acima consultam AGORA. Quem quer escolher o
                                    horário e quanto gastar por noite entra por aqui. */}
                                <button onClick={() => setShowAgendar(true)} disabled={enfileirando} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 disabled:opacity-50" title="Roda no horário que você escolher (ex.: 22h), no ritmo que você definir">
                                    <CalendarClock size={13} /> Agendar…
                                </button>
                                <button onClick={abrirExportCnpjBiz} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100" title="Baixa um .xlsx no modelo de importação do CNPJ Biz (empresa + sócios como contatos)">
                                    <FileSpreadsheet size={13} /> Exportar p/ CNPJ Biz
                                </button>
                                <button onClick={exportarTudo} disabled={exportandoTudo} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 hover:bg-sky-100 disabled:opacity-50" title="Baixa um .xlsx com TUDO que o banco guarda destes leads: cadastro, empresa, sócios, telefones, e-mails, endereços, veículos, participações, vínculos e o JSON bruto">
                                    {exportandoTudo ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {exportandoTudo ? 'Gerando…' : 'Exportar tudo'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Barra de filtros */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3.5 space-y-3">
                        {/* Busca + responsável + limpar */}
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="relative flex-1 min-w-[220px]">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    placeholder="Buscar por nome, CPF/CNPJ, cidade ou telefone…"
                                    className="w-full pl-9 pr-8 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary/40 transition-all"
                                />
                                {busca && (
                                    <button onClick={() => setBusca('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>
                                )}
                            </div>
                            <SearchableSelect
                                value={filtroCidade}
                                onChange={setFiltroCidade}
                                options={opcoesCidade}
                                prefix="Cidade"
                                allLabel={`Todas (${opcoesCidade.length})`}
                                placeholder="Buscar cidade…"
                                className="w-[190px] shrink-0"
                            />
                            <SegmentoFilter selected={filtroSegmento} onChange={setFiltroSegmento} counts={contBuckets} />
                            <DataFilter value={filtroData} onChange={setFiltroData} porDia={contPorDia} />
                            <ResponsibleFilter
                                selectedResponsibles={filtroResp}
                                setSelectedResponsibles={setFiltroResp}
                                profiles={profiles}
                                currentUserProfile={profile}
                                label="Importado para"
                                allLabel="Importado para (todos)"
                                className="shrink-0"
                                align="right"
                            />
                            {(busca || filtroStatus !== 'todos' || filtroEnriq !== 'todos' || filtroResp.length > 0 || filtroCidade || filtroSegmento.length > 0 || filtroData.de || filtroData.ate) && (
                                <button onClick={() => { setBusca(''); setFiltroStatus('todos'); setFiltroEnriq('todos'); setFiltroResp([]); setFiltroCidade(''); setFiltroSegmento([]); setFiltroData(FILTRO_DATA_VAZIO); }}
                                    className="flex items-center gap-1 px-3 py-2 text-xs font-bold rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                                    <X size={13} /> Limpar filtros
                                </button>
                            )}
                        </div>

                        {/* Chips de status */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mr-1 flex items-center gap-1"><Filter size={11} /> Status</span>
                            {([
                                { k: 'todos', l: 'Todos', n: contStatus.todos, c: 'gray' },
                                { k: 'disponivel', l: 'Não importado', n: contStatus.disponivel, c: 'sky' },
                                { k: 'importado', l: 'Importado', n: contStatus.importado, c: 'green' },
                                { k: 'erro', l: 'Com erro', n: contStatus.erro, c: 'red' },
                                { k: 'duplicado', l: 'Duplicado no CRM', n: contStatus.duplicado, c: 'amber' },
                                { k: 'sem_geo', l: 'Sem localização', n: contStatus.semGeo, c: 'orange' },
                                { k: 'com_geo', l: 'No mapa', n: contStatus.comGeo, c: 'blue' },
                            ] as const).map((f) => {
                                const on = filtroStatus === f.k;
                                return (
                                    <button key={f.k} onClick={() => setFiltroStatus(f.k as FiltroStatus)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold border transition-all ${on
                                            ? 'bg-primary text-white border-primary shadow-sm'
                                            : 'bg-gray-50 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary/40'}`}>
                                        {f.l}
                                        <span className={`px-1.5 rounded-full text-[10px] ${on ? 'bg-white/25' : 'bg-gray-200/70 dark:bg-gray-600/60'}`}>{f.n}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Chips de enriquecimento */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mr-1 flex items-center gap-1"><Sparkles size={11} /> Lemit</span>
                            {([
                                { k: 'todos', l: 'Todos', n: contEnriq.todos },
                                { k: 'nao', l: 'Não enriquecido', n: contEnriq.nao },
                                { k: 'empresa', l: 'Falta o dono', n: contEnriq.empresa },
                                { k: 'desempate', l: 'Dono por desempate', n: contEnriq.desempate },
                                { k: 'completo', l: 'Completo', n: contEnriq.completo },
                                { k: 'falhou', l: 'Falhou', n: contEnriq.falhou },
                            ] as const).map((f) => {
                                const on = filtroEnriq === f.k;
                                return (
                                    <button key={f.k} onClick={() => setFiltroEnriq(f.k as FiltroEnriq)}
                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold border transition-all ${on
                                            ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                                            : 'bg-gray-50 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-violet-400'}`}>
                                        {f.l}
                                        <span className={`px-1.5 rounded-full text-[10px] ${on ? 'bg-white/25' : 'bg-gray-200/70 dark:bg-gray-600/60'}`}>{f.n}</span>
                                    </button>
                                );
                            })}
                            <span className="text-xs text-gray-400 ml-auto">Exibindo <b className="text-gray-600 dark:text-gray-300">{rowsFiltradas.length.toLocaleString('pt-BR')}</b> de {rows.length.toLocaleString('pt-BR')}</span>
                        </div>
                    </div>

                    {/* Mapa — mesmo padrão da Busca Geográfica (pins por status, popup com Importar, expandir, seleção por área) */}
                    <div className={`${mapaExpandido ? 'h-[72vh]' : 'h-[260px]'} rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner relative z-0 transition-[height] duration-300`}>
                        <MapContainer center={centro || [-16.68, -49.25]} zoom={11} scrollWheelZoom className="h-full w-full">
                            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                            <MapAutoResize />
                            <FlyTo target={flyTarget || centro} />
                            <BoundsWatcher onBounds={setMapBounds} />
                            <PinsCanvas pins={pins} onClick={clicarPin} />
                            {/* Pin destacado numa camada acima do canvas */}
                            <Pane name="pin-destaque" style={{ zIndex: 450 }}>
                                {pinDestacado && (
                                    <>
                                        <CircleMarker center={[Number(pinDestacado.r.latitude), Number(pinDestacado.r.longitude)]} radius={17} interactive={false}
                                            pathOptions={{ stroke: false, fillColor: '#2563eb', fillOpacity: 0.3 }} />
                                        <CircleMarker center={[Number(pinDestacado.r.latitude), Number(pinDestacado.r.longitude)]} radius={11}
                                            pathOptions={{ color: '#fff', weight: 2, fillColor: PIN_COLOR[pinDestacado.st], fillOpacity: 1 }}
                                            eventHandlers={{ click: () => clicarPin(pinDestacado.r.place_id) }} />
                                    </>
                                )}
                            </Pane>
                            {popupPin && popupRow && popupPos && (
                                <Popup key={popupPin.n} position={popupPos}>
                                    <p className="font-semibold">{popupRow.nome}</p>
                                    <p className="text-xs text-gray-600">{maskDoc(docOf(popupRow))}{popupRow.categoria ? ` · ${popupRow.categoria}` : ''}</p>
                                    {popupRow.dono ? (
                                        <p className="text-xs text-red-600 font-semibold mt-1">Já é lead de {popupRow.dono.responsavel_nome || '—'}</p>
                                    ) : popupRow.status === 'importado' ? (
                                        <p className="text-xs font-semibold text-gray-500 mt-1">Já importado</p>
                                    ) : (
                                        <button
                                            onClick={() => importarUm(popupRow)}
                                            disabled={importando === popupRow.place_id || !firstStage}
                                            className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded bg-primary text-white hover:opacity-90 disabled:opacity-40"
                                        >
                                            <Download size={11} /> {importando === popupRow.place_id ? 'Importando...' : 'Importar'}
                                        </button>
                                    )}
                                    <button onClick={() => openRow(popupRow)} className="block mt-1.5 text-xs font-bold text-primary hover:underline">Ver detalhes</button>
                                </Popup>
                            )}
                        </MapContainer>

                        {/* Controles overlay: expandir + selecionar área */}
                        <div className="absolute top-2 right-2 z-[500] flex flex-col items-end gap-1.5">
                            <button onClick={() => setMapaExpandido((v) => !v)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur text-xs font-bold text-gray-700 dark:text-gray-200 shadow hover:bg-white dark:hover:bg-gray-800">
                                {mapaExpandido ? <><Minimize2 size={12} /> Recolher</> : <><Maximize2 size={12} /> Expandir mapa</>}
                            </button>
                            <button onClick={selecionarNaArea} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur text-xs font-bold text-primary shadow hover:bg-white dark:hover:bg-gray-800">
                                <BoxSelect size={12} /> Selecionar área
                            </button>
                        </div>

                        {/* Legenda de status dos pins */}
                        <div className="absolute bottom-2 left-2 z-[500] flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur text-[10px] font-semibold text-gray-600 dark:text-gray-300 shadow">
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#f97316' }} /> disponível</span>
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#2563eb' }} /> selecionado</span>
                            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#9ca3af' }} /> importado</span>
                        </div>
                    </div>

                    {/* Lista */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                            {/* Seleção serve tanto p/ enriquecer (vale p/ importados) quanto p/ importar
                                (o import filtra só os importáveis) — por isso dá p/ marcar tudo. */}
                            <div className="flex items-center gap-3 flex-wrap">
                                <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={rowsFiltradas.length > 0 && rowsFiltradas.every((r) => selPlaces.includes(r.place_id))}
                                        onChange={(e) => setSelPlaces(e.target.checked ? rowsFiltradas.map((r) => r.place_id) : [])}
                                        className="rounded text-primary focus:ring-primary"
                                    />
                                    Selecionar tudo do filtro ({rowsFiltradas.length.toLocaleString('pt-BR')})
                                </label>
                                {importaveisFiltrados.length > 0 && (
                                    <button
                                        onClick={() => setSelPlaces(importaveisFiltrados.map((r) => r.place_id))}
                                        className="text-xs font-semibold text-primary hover:underline"
                                    >
                                        só importáveis ({importaveisFiltrados.length})
                                    </button>
                                )}
                                {selPlaces.length > 0 && (
                                    <button onClick={() => setSelPlaces([])} className="text-xs font-semibold text-gray-400 hover:underline">
                                        limpar seleção
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <ResponsibleFilter
                                    selectedResponsibles={vendedoresImport}
                                    setSelectedResponsibles={setVendedoresImport}
                                    profiles={profiles}
                                    currentUserProfile={profile}
                                    label="Importar para mim"
                                    allLabel="Importar para mim"
                                    className="shrink-0"
                                    align="right"
                                />
                                {alvoImportCount > 0 && (
                                    <button onClick={() => setShowRotaConfig(true)} disabled={importandoLote || !firstStage} className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-full bg-primary text-white hover:opacity-90 disabled:opacity-40">
                                        <Download size={12} /> {selCount ? `Importar selecionados (${alvoImportCount})` : `Importar todos (${alvoImportCount})`}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            {pageRows.map((r) => {
                                const importavel = isImportavel(r);
                                return (
                                    <div key={r.place_id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${selPlaces.includes(r.place_id) ? 'bg-primary/5 border-primary/30' : 'bg-gray-50/60 dark:bg-gray-700/30 border-transparent hover:border-gray-200 dark:hover:border-gray-600'}`}
                                        onClick={() => openRow(r)}>
                                        {/* checkbox em TODAS as linhas: a seleção também serve p/ enriquecer */}
                                        <input
                                            type="checkbox"
                                            checked={selPlaces.includes(r.place_id)}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => setSelPlaces((prev) => (prev.includes(r.place_id) ? prev.filter((id) => id !== r.place_id) : [...prev, r.place_id]))}
                                            className="rounded text-primary focus:ring-primary shrink-0"
                                            title={importavel ? 'Selecionar' : 'Selecionar (só para enriquecer — já importado/duplicado)'}
                                        />
                                        {!importavel && <span className="sr-only">já importado</span>}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{r.nome}</p>
                                                <span className="text-[10px] font-mono text-gray-400">{maskDoc(docOf(r))}</span>
                                                <span className="px-1 py-0.5 text-[8px] font-bold rounded bg-gray-100 dark:bg-gray-700 text-gray-500">{(r.doc_tipo || docTipoFromDigits(docOf(r))) === 'cnpj' ? 'PJ' : 'PF'}</span>
                                                {r.latitude != null && <MapPin size={12} className="text-sky-500" />}
                                                {r.enriquecimento_status !== 'nao' && (
                                                    <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                                                        {r.enriquecimento_status === 'completo' ? 'dono ✓' : r.enriquecimento_status === 'falhou' ? 'falhou' : 'empresa ✓'}
                                                    </span>
                                                )}
                                                {r.import_erro && (
                                                    <span className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" title={r.import_erro}>
                                                        <AlertTriangle size={9} /> erro
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{r.categoria}{r.endereco ? ` · ${r.endereco}` : ''}</p>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                {(r.enriquecimento_data?.melhor_whatsapp || r.telefone) && <span className="text-[11px] text-gray-500">{fmtFone(r.enriquecimento_data?.melhor_whatsapp || r.telefone)}</span>}
                                                {(r.enriquecimento_data?.melhor_email || r.email) && <span className="text-[11px] text-gray-400 truncate">{r.enriquecimento_data?.melhor_email || r.email}</span>}
                                                {r.importadoPara && <span className="text-[10px] text-green-600 dark:text-green-400">→ {r.importadoPara.nome}</span>}
                                                {r.criadoDia && (
                                                    <span className="flex items-center gap-1 text-[10px] text-gray-400" title={`Entrou na lista em ${new Date(r.created_at!).toLocaleString('pt-BR')}`}>
                                                        <CalendarDays size={10} /> {fmtDia(r.criadoDia)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Enriquecer e importar são independentes: um lead JÁ IMPORTADO
                                            ainda pode precisar do CPF do dono. Por isso a ação de
                                            enriquecimento aparece sempre, ao lado do status de import. */}
                                        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                            {(() => {
                                                const passo = proximoPassoEnriquecimento(r);
                                                const rodando = enriquecendoUm === r.place_id;
                                                if (passo === 'completo') {
                                                    // Dono decidido no cara ou coroa: mostra o selo diferente
                                                    // para dar a chance de conferir, sem exigir nada.
                                                    if (ehDesempate(r)) {
                                                        return (
                                                            <button onClick={() => openRow(r)}
                                                                className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100"
                                                                title="Todos os sócios tinham a mesma participação — o dono foi escolhido por desempate. Clique para trocar.">
                                                                <Users size={12} /> Dono por desempate
                                                            </button>
                                                        );
                                                    }
                                                    return (
                                                        <span className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400" title="Enriquecimento concluído">
                                                            <Check size={12} /> Enriquecido
                                                        </span>
                                                    );
                                                }
                                                const label = passo === 'buscar_dono' ? 'Buscar dono' : passo === 'tentar_novamente' ? 'Tentar de novo' : 'Enriquecer';
                                                const tint = passo === 'buscar_dono'
                                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100'
                                                    : 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 hover:bg-violet-100';
                                                return (
                                                    <button onClick={() => enriquecerUm(r)}
                                                        // Antes ficava travado enquanto o lote rodava; agora o lote é do
                                                        // servidor e o worker reconfere a cota a cada item, então uma
                                                        // consulta avulsa aqui não atrapalha.
                                                        disabled={rodando}
                                                        className={`flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full disabled:opacity-40 ${tint}`}
                                                        title={passo === 'buscar_dono' ? 'Empresa já consultada — falta o CPF do dono' : 'Enriquecer na Lemit'}>
                                                        {rodando ? <Loader2 size={12} className="animate-spin" /> : passo === 'buscar_dono' ? <Users size={12} /> : <Sparkles size={12} />}
                                                        {label}
                                                    </button>
                                                );
                                            })()}

                                            {r.status === 'importado' ? (
                                                <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                    <Check size={12} /> Importado
                                                </span>
                                            ) : r.dono ? (
                                                <span className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" title="Bloqueado por duplicidade">
                                                    <UserCheck size={12} /> Lead de {r.dono.responsavel_nome || '—'}
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => importarUm(r)}
                                                    disabled={importando === r.place_id || !firstStage}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold rounded-full bg-primary text-white hover:opacity-90 disabled:opacity-40"
                                                >
                                                    {importando === r.place_id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Lead
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {pageRows.length === 0 && (
                                <p className="text-center py-10 text-sm text-gray-400">Nenhum lead corresponde ao filtro.</p>
                            )}
                        </div>

                        {/* Paginação — sticky no rodapé do card p/ nunca sumir com listas longas */}
                        <div className="sticky bottom-0 -mx-4 -mb-4 mt-3 px-4 py-2.5 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-t border-gray-100 dark:border-gray-700 rounded-b-2xl flex flex-wrap items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {rowsFiltradas.length === 0 ? 'Nenhum resultado'
                                    : <>Mostrando <b className="text-gray-700 dark:text-gray-200">{inicio + 1}–{Math.min(inicio + pageSize, rowsFiltradas.length)}</b> de <b className="text-gray-700 dark:text-gray-200">{rowsFiltradas.length.toLocaleString('pt-BR')}</b></>}
                            </span>

                            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ml-2">
                                Por página:
                                <select
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(Number(e.target.value)); setPagina(0); }}
                                    className="text-xs font-bold px-2 py-1 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                                >
                                    {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>

                            <div className="flex items-center gap-1.5 ml-auto">
                                <button onClick={() => setPagina(0)} disabled={paginaAtual === 0}
                                    className="px-2 py-1.5 text-xs font-bold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 disabled:opacity-40" title="Primeira página">«</button>
                                <button onClick={() => setPagina((p) => Math.max(0, p - 1))} disabled={paginaAtual === 0}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 disabled:opacity-40">
                                    <ChevronLeft size={14} /> Anterior
                                </button>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 px-1.5">{paginaAtual + 1}<span className="text-gray-400 font-normal"> / {totalPaginas}</span></span>
                                <button onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))} disabled={paginaAtual >= totalPaginas - 1}
                                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 disabled:opacity-40">
                                    Próxima <ChevronRight size={14} />
                                </button>
                                <button onClick={() => setPagina(totalPaginas - 1)} disabled={paginaAtual >= totalPaginas - 1}
                                    className="px-2 py-1.5 text-xs font-bold rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 disabled:opacity-40" title="Última página">»</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <RotaConfigModal
                isOpen={showRotaConfig}
                qtdLeads={alvoImportCount}
                kwhTotal={0}
                destinosLabel={destNome}
                destinosCount={destinos().length}
                onClose={() => setShowRotaConfig(false)}
                onConfirmRota={agendarVisitas}
                onImportarSemRota={importarLoteSimples}
            />

            <ExportCnpjBizModal
                isOpen={showExport}
                onClose={() => setShowExport(false)}
                total={alvosExport.length}
                comDono={alvosExport.filter((r) => r.enriquecimento_status === 'completo').length}
                emailPadrao={profile?.email}
                onConfirm={confirmarExportCnpjBiz}
            />

            <AgendarLoteModal
                isOpen={showAgendar}
                onClose={() => setShowAgendar(false)}
                disponiveis={(() => {
                    const a = alvosPorTipo();
                    return { geocode: a.geocode.length, lemit_documento: a.lemit_documento.length, lemit_dono: a.lemit_dono.length };
                })()}
                custoPessoa={custoPessoa}
                custoEmpresa={custoEmpresa}
                salvando={enfileirando}
                onConfirm={confirmarAgendamento}
            />

            <ProspeccaoRowModal
                row={selectedRow}
                onClose={() => setSelectedRow(null)}
                onEnrich={enriquecerUm}
                onImport={importarUm}
                onPatch={(upd) => {
                    patchRow(upd.place_id, upd);
                    setSelectedRow((s) => (s && s.place_id === upd.place_id ? { ...s, ...upd } : s));
                }}
                enriching={selectedRow ? enriquecendoUm === selectedRow.place_id : false}
                importing={selectedRow ? importando === selectedRow.place_id : false}
            />
        </div>
    );
};

export default ProspeccaoUploadView;
